import { ChangeDetectionStrategy, Component, effect } from '@angular/core';
import { BackendService } from '@app/@shared/backend.service';
import { SecureFileService } from '@app/@shared/secure-file.service';
import { ProjectService } from '@app/shell/header/header.project.service';
import { TableExportService } from '@shared/table-export.service';
import type { ExportColumn, ExportRow } from '@shared/table-export.service';
import {
  normalizeNumericTableRows,
  isNumericTableColumn,
  SMALL_TABLE_FILTER_BUTTON_PROPS,
} from '@shared/table-filter.utils';
import { CAPTURE_NAV_CONFIG } from '@shared/analysis-nav/nav-configs';
import { NavConfig } from '@shared/analysis-nav/analysis-nav.component';
import { FilterMatchMode } from '@openng/optimus-ui/api';
import type { FilterMetadata } from '@openng/optimus-ui/api';
import type { Table, TableFilterEvent } from '@openng/optimus-ui/table';

type TableFilters = Record<string, FilterMetadata | FilterMetadata[]>;

interface SummaryLink {
  dataLink: string;
  sample: string | null;
  label: string;
}

interface CoverageRow extends ExportRow {
  docLinkItemsByColumn: Record<string, SummaryLink[]>;
}

interface SummaryData {
  desc: string;
  coverage_cols: ExportColumn[];
  coverage_data: CoverageRow[];
  gene_counts_cols: ExportColumn[];
  gene_counts: ExportRow[];
}

type SummaryTableId = 'coverage' | 'geneCounts';

@Component({
  // This component is activated through routing and is not used as an element selector.
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'gene-summary',
  templateUrl: './gene-summary.component.html',
  styleUrls: ['./gene-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class GeneSummaryComponent {
  isLoading = false;
  projectViewData: SummaryData = {
    desc: '',
    coverage_cols: [],
    coverage_data: [],
    gene_counts_cols: [],
    gene_counts: [],
  };
  readonly pageSizes = [10, 20, 50, 200, 500];
  exportMessages: Record<SummaryTableId, string> = { coverage: '', geneCounts: '' };
  selectedProjectID = '';
  coverageFilters: TableFilters = this.createTableFilters();
  geneCountsFilters: TableFilters = this.createTableFilters();
  coverageGlobalFilter = '';
  geneCountsGlobalFilter = '';
  filteredCoverageRows: CoverageRow[] = [];
  filteredGeneCountRows: ExportRow[] = [];
  readonly filterButtonProps = SMALL_TABLE_FILTER_BUTTON_PROPS;

  private projectRequestId = 0;

  get navConfig(): NavConfig {
    return CAPTURE_NAV_CONFIG;
  }

  get analysisType(): 'capture' | 'flairr' {
    return 'capture';
  }

  get coverageGlobalFilterFields(): string[] {
    return this.projectViewData.coverage_cols.map((column) => column.data);
  }

  get geneCountsGlobalFilterFields(): string[] {
    return this.projectViewData.gene_counts_cols.map((column) => column.data);
  }

  constructor(
    private backendService: BackendService,
    public projectService: ProjectService,
    private secureFileService: SecureFileService,
    private exportService: TableExportService,
  ) {
    effect(() => {
      this.setProject();
    });
  }

  setProject(): void {
    const projectDetails = this.projectService.selectedProjectDetails();
    if (projectDetails === null) {
      this.resetProjectData();
      return;
    }

    const requestId = ++this.projectRequestId;
    this.selectedProjectID = projectDetails.project_id;
    this.isLoading = true;
    this.exportMessages = { coverage: '', geneCounts: '' };
    this.projectViewData = this.emptySummaryData();
    this.filteredCoverageRows = [];
    this.filteredGeneCountRows = [];
    this.resetTableFilters();

    this.backendService.getProjectSummaryData(this.selectedProjectID, 'Capture Probe').subscribe({
      next: (response: unknown) => {
        if (requestId !== this.projectRequestId) {
          return;
        }

        this.isLoading = false;
        if (!this.isRecord(response) || Number(response['status']) !== 200) {
          return;
        }

        const summaryData = this.parseSummaryData(response['reply']);
        if (summaryData === null) {
          this.exportMessages = {
            coverage: 'The summary statistics response was not in the expected format.',
            geneCounts: '',
          };
          console.error('The summary statistics endpoint returned an invalid response.', response);
          return;
        }

        this.projectViewData = summaryData;
        this.filteredCoverageRows = this.projectViewData.coverage_data;
        this.filteredGeneCountRows = this.projectViewData.gene_counts;
        this.resetTableFilters();
      },
      error: (error: unknown) => {
        if (requestId !== this.projectRequestId) {
          return;
        }

        this.isLoading = false;
        console.error('Failed to load project summary statistics.', error);
      },
    });
  }

  openDocLink(event: MouseEvent): void {
    this.secureFileService.handleCellClick(event);
  }

  getDocLinks(row: CoverageRow, column: string): SummaryLink[] {
    return row.docLinkItemsByColumn[column] || [];
  }

  isCoverageNumericColumn(column: ExportColumn): boolean {
    return isNumericTableColumn(column, this.projectViewData.coverage_data);
  }

  isReportsColumn(column: ExportColumn): boolean {
    return this.projectViewData.coverage_data.some((row) => this.getDocLinks(row, column.data).length > 0);
  }

  isGeneCountsNumericColumn(column: ExportColumn): boolean {
    return isNumericTableColumn(column, this.projectViewData.gene_counts);
  }

  onGlobalFilterChange(tableId: SummaryTableId, event: Event, table: Table): void {
    const value = (event.target as HTMLInputElement).value;
    if (tableId === 'coverage') {
      this.coverageGlobalFilter = value;
    } else {
      this.geneCountsGlobalFilter = value;
    }
    table.filterGlobal(value, FilterMatchMode.CONTAINS);
  }

  onTableFilter(table: SummaryTableId, event: TableFilterEvent): void {
    if (table === 'coverage') {
      const rows = (event.filteredValue as ExportRow[] | undefined) ?? this.projectViewData.coverage_data;
      this.filteredCoverageRows = rows as CoverageRow[];
    } else {
      this.filteredGeneCountRows = (event.filteredValue as ExportRow[] | undefined) ?? this.projectViewData.gene_counts;
    }
  }

  async copyTable(table: SummaryTableId): Promise<void> {
    const { columns, rows } = this.tableData(table);
    await this.runExport(table, 'copy', () => this.exportService.copy(columns, rows));
  }

  async exportExcel(table: SummaryTableId): Promise<void> {
    const { columns, rows, filename } = this.tableData(table);
    await this.runExport(table, 'Excel', () => this.exportService.exportExcel(columns, rows, filename));
  }

  async exportPdf(table: SummaryTableId): Promise<void> {
    const { columns, rows, filename } = this.tableData(table);
    await this.runExport(table, 'PDF', () => this.exportService.exportPdf(columns, rows, filename));
  }

  private async runExport(table: SummaryTableId, label: string, action: () => Promise<void>): Promise<void> {
    this.exportMessages = { ...this.exportMessages, [table]: '' };
    try {
      await action();
      this.exportMessages = {
        ...this.exportMessages,
        [table]: label === 'copy' ? 'Table copied to clipboard.' : `${label} export downloaded.`,
      };
    } catch (error) {
      this.exportMessages = {
        ...this.exportMessages,
        [table]: `Could not complete ${label === 'copy' ? 'copy' : `${label} export`}.`,
      };
      console.error(`Failed to complete ${label} for ${table} summary data.`, error);
    }
  }

  private tableData(table: SummaryTableId): {
    columns: ExportColumn[];
    rows: ExportRow[];
    filename: string;
  } {
    if (table === 'coverage') {
      const rows = this.filteredCoverageRows.map((row) => {
        const { docLinkItemsByColumn, ...exportRow } = row;
        for (const [column, links] of Object.entries(docLinkItemsByColumn)) {
          exportRow[column] = links.map((link) => link.label).join(', ');
        }
        return exportRow;
      });
      return { columns: this.projectViewData.coverage_cols, rows, filename: 'gene-coverage' };
    }

    return {
      columns: this.projectViewData.gene_counts_cols,
      rows: this.filteredGeneCountRows,
      filename: 'gene-counts',
    };
  }

  private parseSummaryData(value: unknown): SummaryData | null {
    if (
      !this.isRecord(value) ||
      typeof value['desc'] !== 'string' ||
      !Array.isArray(value['coverage_cols']) ||
      !Array.isArray(value['coverage_data']) ||
      !Array.isArray(value['gene_counts_cols']) ||
      !Array.isArray(value['gene_counts'])
    ) {
      return null;
    }

    const coverageColumns = value['coverage_cols'].filter((column) => this.isExportColumn(column));
    const geneCountColumns = value['gene_counts_cols'].filter((column) => this.isExportColumn(column));
    const coverageRows = value['coverage_data'].filter((row) => this.isRecord(row));
    const geneCountRows = value['gene_counts'].filter((row) => this.isRecord(row));
    if (
      coverageColumns.length !== value['coverage_cols'].length ||
      geneCountColumns.length !== value['gene_counts_cols'].length ||
      coverageRows.length !== value['coverage_data'].length ||
      geneCountRows.length !== value['gene_counts'].length
    ) {
      return null;
    }

    const coverageData = coverageRows.map((row) => {
      const docLinkItemsByColumn: Record<string, SummaryLink[]> = {};
      for (const column of coverageColumns) {
        const links = this.parseDocLinks(row[column.data]);
        if (links.length > 0) {
          docLinkItemsByColumn[column.data] = links;
        }
      }
      return { ...row, docLinkItemsByColumn };
    });

    return {
      desc: value['desc'],
      coverage_cols: coverageColumns,
      coverage_data: normalizeNumericTableRows(coverageColumns, coverageData),
      gene_counts_cols: geneCountColumns,
      gene_counts: normalizeNumericTableRows(geneCountColumns, geneCountRows),
    };
  }

  private parseDocLinks(value: unknown): SummaryLink[] {
    if (typeof value !== 'string' || !value.trim()) {
      return [];
    }

    const document = new DOMParser().parseFromString(value, 'text/html');
    return Array.from(document.querySelectorAll<HTMLElement>('[data-link]'))
      .map((element) => ({
        dataLink: element.getAttribute('data-link') || '',
        sample: element.getAttribute('data-sample'),
        label: element.textContent?.trim() || '',
      }))
      .filter((link) => link.dataLink.length > 0 && link.label.length > 0);
  }

  private isExportColumn(value: unknown): value is ExportColumn {
    return this.isRecord(value) && typeof value['title'] === 'string' && typeof value['data'] === 'string';
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  private emptySummaryData(): SummaryData {
    return {
      desc: '',
      coverage_cols: [],
      coverage_data: [],
      gene_counts_cols: [],
      gene_counts: [],
    };
  }

  private createTableFilters(): TableFilters {
    return { global: { value: null, matchMode: FilterMatchMode.CONTAINS } };
  }

  private resetTableFilters(): void {
    this.coverageFilters = this.createTableFilters();
    this.geneCountsFilters = this.createTableFilters();
    this.coverageGlobalFilter = '';
    this.geneCountsGlobalFilter = '';
  }

  private resetProjectData(): void {
    this.projectRequestId += 1;
    this.selectedProjectID = '';
    this.isLoading = false;
    this.exportMessages = { coverage: '', geneCounts: '' };
    this.projectViewData = this.emptySummaryData();
    this.filteredCoverageRows = [];
    this.filteredGeneCountRows = [];
    this.resetTableFilters();
  }
}
